
import type { TrackName } from "../audio/music";
import { elem, styleInstaller } from "./chrome";

export type GraphicsQuality = "standard" | "sharp";
/** The original system panel's two `SetCameraMaxDistance` choices. */
export type CameraDistance = "short" | "long";
export type NameColorMode = "normal" | "empire";

export interface ClientOptions {
  readonly musicVolume: number;
  /** A player-selected field theme. This stays local, like the 2014 setting. */
  readonly musicTrack: TrackName;
  readonly soundVolume: number;
  readonly graphicsQuality: GraphicsQuality;
  readonly cameraDistance: CameraDistance;
  /** Local presentation choices from `gameoptiondialog.py`; none are protocol
   *  state -- see `ui/gameoptions.ts`. */
  readonly targetBoardVisible: boolean;
  readonly alwaysShowNames: boolean;
  readonly showDamage: boolean;
  readonly showSalesText: boolean;
  readonly viewChat: boolean;
  /** `constInfo.CHRNAME_COLOR_INDEX`: Normal or Kingdom Color nameplates. */
  readonly nameColorMode: NameColorMode;
}

export const DEFAULT_CLIENT_OPTIONS: ClientOptions = {
  musicVolume: 0.4,
  musicTrack: "m2bg",
  soundVolume: 0.6,
  graphicsQuality: "sharp",
  cameraDistance: "short",
  targetBoardVisible: true,
  alwaysShowNames: false,
  showDamage: true,
  showSalesText: true,
  viewChat: true,
  nameColorMode: "normal",
};

function isNameColorModeValue(value: unknown): value is NameColorMode {
  return value === "normal" || value === "empire";
}

const STORAGE_KEY = "metin3.options.v1";

export function browserStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // A sandboxed iframe can throw while the property itself is read.
    return null;
  }
}

function clamp01(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(0, Math.min(1, value))
    : fallback;
}

/** Read the one persisted preference blob without allowing blocked/corrupt
 *  storage to stop boot. Both option panels share it -- see `OptionsStore`. */
export function loadClientOptions(
  storage: Storage | null = browserStorage(),
): ClientOptions {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    if (raw === null || raw === undefined) {
      return { ...DEFAULT_CLIENT_OPTIONS };
    }
    const value = JSON.parse(raw) as Partial<ClientOptions>;
    return {
      musicVolume: clamp01(value.musicVolume, DEFAULT_CLIENT_OPTIONS.musicVolume),
      musicTrack: isTrackName(value.musicTrack) ? value.musicTrack : DEFAULT_CLIENT_OPTIONS.musicTrack,
      soundVolume: clamp01(value.soundVolume, DEFAULT_CLIENT_OPTIONS.soundVolume),
      graphicsQuality:
        value.graphicsQuality === "standard" || value.graphicsQuality === "sharp"
          ? value.graphicsQuality
          : DEFAULT_CLIENT_OPTIONS.graphicsQuality,
      cameraDistance:
        value.cameraDistance === "short" || value.cameraDistance === "long"
          ? value.cameraDistance
          : DEFAULT_CLIENT_OPTIONS.cameraDistance,
      targetBoardVisible: typeof value.targetBoardVisible === "boolean" ? value.targetBoardVisible : DEFAULT_CLIENT_OPTIONS.targetBoardVisible,
      alwaysShowNames: typeof value.alwaysShowNames === "boolean" ? value.alwaysShowNames : DEFAULT_CLIENT_OPTIONS.alwaysShowNames,
      showDamage: typeof value.showDamage === "boolean" ? value.showDamage : DEFAULT_CLIENT_OPTIONS.showDamage,
      showSalesText: typeof value.showSalesText === "boolean" ? value.showSalesText : DEFAULT_CLIENT_OPTIONS.showSalesText,
      viewChat: typeof value.viewChat === "boolean" ? value.viewChat : DEFAULT_CLIENT_OPTIONS.viewChat,
      nameColorMode: isNameColorModeValue(value.nameColorMode)
        ? value.nameColorMode
        : DEFAULT_CLIENT_OPTIONS.nameColorMode,
    };
  } catch {
    return { ...DEFAULT_CLIENT_OPTIONS };
  }
}

/** Best effort by design: a display preference is never worth breaking play. */
export function saveClientOptions(options: ClientOptions, storage: Storage | null): void {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(options));
  } catch {
    // Private browsing and cookie policy can deny storage after page load.
  }
}

export function pixelRatioCap(quality: GraphicsQuality): number {
  return quality === "standard" ? 1 : 2;
}

export interface VolumeControl {
  volume: number;
}

export const MUSIC_TRACKS: readonly (readonly [TrackName, string])[] = [
  ["m2bg", "Metin2 theme"],
  ["enter_the_east", "Enter the East"],
  ["login_window", "Login window"],
  ["characterselect", "Character select"],
  ["wedding", "Wedding"],
] as const;

export function isTrackName(value: unknown): value is TrackName {
  return MUSIC_TRACKS.some(([track]) => track === value);
}

export interface MusicControl extends VolumeControl {
  select(track: TrackName): void;
}

export interface SystemOptionsActions {
  readonly music: MusicControl;
  readonly sound: VolumeControl;
  readonly rendererBackend: string;
  readonly onGraphicsQuality: (quality: GraphicsQuality) => void;
  /** Apply the browser-local source camera cap immediately. */
  readonly onCameraDistance: (distance: CameraDistance) => void;
  /** Keep the standalone cog for builds without the enclosing system menu. */
  readonly showTrigger?: boolean;
}

/** This panel's own reference, not a `systemoptiondialog.py` control -- see
 *  the module doc's last paragraph. */
export const KEY_BINDINGS: readonly (readonly [action: string, key: string])[] = [
  ["Move", "W A S D / arrows"],
  ["Walk", "Shift"],
  ["Attack", "Space"],
  ["Turn camera", "Q / E"],
  ["Use skill", "1–9"],
  ["Use item", "F1–F4"],
  ["Inventory", "I"],
  ["Character", "C"],
  ["Pick up", "F"],
  ["World map", "M"],
  ["Guild", "G"],
  ["Guild emblem", "U"],
  ["Auction", "Shift + U"],
  ["Horse", "H"],
  ["Train / restart", "R"],
  ["Fishing / cast", "N / J"],
  ["Mining / swing", "L / K"],
  ["Dungeon door", "X"],
  ["Gestures", "B"],
  ["Invite to guild", "O"],
  ["Invite to party", "P"],
  ["Trade", "T"],
  ["Private shop", "Y"],
  ["Social", "Alt + M"],
  ["Company", "Shift + J"],
  ["Item mall", "Shift + K"],
  ["Chat", "Enter"],
] as const;

const STYLE_ID = "sysoptions-style";
const STYLE = `
.options-open {
  position: fixed; right: max(12px, env(safe-area-inset-right));
  top: max(12px, env(safe-area-inset-top)); z-index: 70;
  width: 40px; height: 36px; border: 1px solid #8d7044; border-radius: 4px;
  color: #ead7ae; background: linear-gradient(#3b3023, #18140f);
  box-shadow: inset 0 0 0 1px rgba(255,220,160,.12), 0 3px 12px #0009;
  pointer-events: auto; cursor: pointer; font: 18px/1 serif;
}
.options-panel {
  position: fixed; right: max(12px, env(safe-area-inset-right));
  top: max(58px, calc(env(safe-area-inset-top) + 58px)); z-index: 69;
  width: min(328px, calc(100vw - 24px)); max-height: calc(100vh - 76px);
  overflow: auto; padding: 9px; pointer-events: auto;
  border: 1px solid #8d7044; border-radius: 5px; color: #eadfca;
  background: linear-gradient(145deg, rgba(48,39,28,.97), rgba(15,13,11,.97));
  box-shadow: inset 0 0 0 1px rgba(255,220,160,.1), 0 12px 32px #000b;
  font: 12px/1.35 system-ui, sans-serif;
}
.options-title { display:flex; align-items:center; justify-content:space-between;
  padding: 2px 3px 8px; color:#f1d69e; text-transform:uppercase;
  letter-spacing:.1em; font-weight:700; }
.options-close { border:0; color:#d8c7a6; background:transparent; cursor:pointer;
  font:18px/1 serif; }
.options-group { margin:0 0 8px; padding:9px; border:1px solid rgba(184,145,83,.35);
  background:rgba(0,0,0,.18); }
.options-group legend { padding:0 5px; color:#c7a66c; text-transform:uppercase;
  letter-spacing:.08em; font-size:10px; }
.options-row { display:grid; grid-template-columns: 88px 1fr 34px; align-items:center;
  gap:8px; margin:5px 0; }
.options-row output { text-align:right; color:#cdbb98; font-variant-numeric:tabular-nums; }
.options-range { width:100%; accent-color:#b99355; }
.options-select { width:100%; padding:4px 6px; border:1px solid #735b36;
  border-radius:3px; color:#eadfca; background:#201a13; }
.options-note { margin:5px 0 0; color:#9f927d; font-size:10px; }
.options-radio { display:inline-flex; align-items:center; gap:4px; margin-right:12px; cursor:pointer; }
.options-radio input { accent-color:#b99355; }
.options-inert { opacity:.55; }
.options-inert-button { padding:4px 10px; border:1px solid #5b4a30; border-radius:3px;
  color:#8b7a5c; background:#241d14; cursor:not-allowed; }
.options-keys { display:grid; grid-template-columns:1fr auto; gap:4px 12px; margin:0; }
.options-keys dt { color:#cbb994; }
.options-keys dd { margin:0; color:#f0dbad; text-align:right; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export class SystemOptionsPanel {
  private readonly root: HTMLElement;
  private readonly storage: Storage | null;
  private value: ClientOptions;

  constructor(
    mount: HTMLElement,
    private readonly actions: SystemOptionsActions,
    storage: Storage | null = browserStorage(),
    initial: ClientOptions = loadClientOptions(storage),
  ) {
    const doc = mount.ownerDocument;
    installStyle(doc);
    this.storage = storage;
    this.value = { ...initial };

    const open = doc.createElement("button");
    open.type = "button";
    open.className = "options-open";
    open.title = "System options";
    open.setAttribute("aria-label", "Open system options");
    open.textContent = "⚙";

    this.root = elem(doc, "section", "options-panel");
    this.root.setAttribute("aria-label", "System options");
    this.root.hidden = true;
    // systemoptiondialog.py draws only its labels. The music dropdown stands in
    // for uiSelectMusic's folder picker (uisystemoption.py:63-65,100-102); Fog
    // (uisystemoption.py:172-189) and Tiling (uisystemoption.py:122-141, whose
    // Apply only takes effect after net.ExitGame) are drawn inert.
    this.root.innerHTML = `
      <header class="options-title"><span>System options</span><button type="button" class="options-close" aria-label="Close system options">×</button></header>
      <fieldset class="options-group"><legend>Sound Effects</legend>
        <label class="options-row"><span>Effects</span><input class="options-range" data-option="sound" type="range" min="0" max="100" step="1"><output data-output="sound"></output></label>
      </fieldset>
      <fieldset class="options-group"><legend>Music</legend>
        <label class="options-row"><span>Volume</span><input class="options-range" data-option="music" type="range" min="0" max="100" step="1"><output data-output="music"></output></label>
        <label class="options-row"><span>Change</span><select class="options-select" data-option="music-track"></select><output></output></label>
      </fieldset>
      <fieldset class="options-group"><legend>Camera</legend>
        <label class="options-row"><span>Range</span><select class="options-select" data-option="camera"><option value="short">Close</option><option value="long">Far</option></select><output></output></label>
      </fieldset>
      <fieldset class="options-group"><legend>Resolution</legend>
        <label class="options-row"><span>Backing buffer</span><select class="options-select" data-option="graphics"><option value="standard">Standard</option><option value="sharp">Sharp</option></select><output></output></label>
        <p class="options-note">${actions.rendererBackend.toUpperCase()} renderer · applies immediately</p>
      </fieldset>
      <fieldset class="options-group options-inert"><legend>Fog</legend>
        <label class="options-radio"><input type="radio" name="sysoptions-fog" disabled> Dense</label>
        <label class="options-radio"><input type="radio" name="sysoptions-fog" disabled> Middle</label>
        <label class="options-radio"><input type="radio" name="sysoptions-fog" disabled checked> Light</label>
      </fieldset>
      <fieldset class="options-group options-inert"><legend>Tiling</legend>
        <label class="options-radio"><input type="radio" name="sysoptions-tiling" disabled> CPU</label>
        <label class="options-radio"><input type="radio" name="sysoptions-tiling" disabled checked> GPU</label>
        <button type="button" class="options-inert-button" disabled>Apply</button>
      </fieldset>
      <fieldset class="options-group"><legend>Key bindings</legend><dl class="options-keys"></dl>
        <p class="options-note">Wheel or pinch zooms the camera.</p>
      </fieldset>`;

    const keys = this.root.querySelector(".options-keys");
    for (const [action, key] of KEY_BINDINGS) {
      const dt = doc.createElement("dt");
      const dd = doc.createElement("dd");
      dt.textContent = action;
      dd.textContent = key;
      keys?.append(dt, dd);
    }

    open.addEventListener("click", () => this.setVisible(!this.showing));
    this.root.querySelector(".options-close")?.addEventListener("click", () => this.setVisible(false));
    this.bindRange("music", (volume) => (this.actions.music.volume = volume));
    this.bindRange("sound", (volume) => (this.actions.sound.volume = volume));
    this.root.querySelector<HTMLSelectElement>('[data-option="graphics"]')?.addEventListener("change", (event) => {
      const quality = (event.currentTarget as HTMLSelectElement).value as GraphicsQuality;
      if (quality !== "standard" && quality !== "sharp") return;
      this.value = { ...this.value, graphicsQuality: quality };
      this.actions.onGraphicsQuality(quality);
      this.persist();
    });
    this.root.querySelector<HTMLSelectElement>('[data-option="camera"]')?.addEventListener("change", (event) => {
      const distance = (event.currentTarget as HTMLSelectElement).value;
      if (distance !== "short" && distance !== "long") return;
      this.value = { ...this.value, cameraDistance: distance };
      this.actions.onCameraDistance(distance);
      this.persist();
    });
    const tracks = this.root.querySelector<HTMLSelectElement>('[data-option="music-track"]');
    for (const [track, label] of MUSIC_TRACKS) {
      const option = doc.createElement("option");
      option.value = track;
      option.textContent = label;
      tracks?.append(option);
    }
    tracks?.addEventListener("change", (event) => {
      const track = (event.currentTarget as HTMLSelectElement).value;
      if (!isTrackName(track)) return;
      this.value = { ...this.value, musicTrack: track };
      // Mirrors `uisystemoption.py:191-205`: persist the selected filename,
      // then ask audio to fade from the old field music to the replacement.
      // `Music` still owns autoplay refusal; a select never claims it started.
      this.actions.music.select(track);
      this.persist();
    });
    // `SystemOptionDialog.OnPressEscapeKey` closes the option window and
    // consumes Escape (`uisystemoption.py:231-233`).
    const onEscape = (event: KeyboardEvent) => {
      if (!this.showing || event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      this.setVisible(false);
      open.focus();
    };
    this.root.addEventListener("keydown", onEscape);
    doc.addEventListener("keydown", (event) => {
      if (!this.root.isConnected) return;
      onEscape(event);
    });
    // The system menu owns the one visible entry point in the game HUD. Keep
    // the cog as the default for callers that embed this panel on its own,
    // while allowing the composed menu to avoid competing buttons.
    if (actions.showTrigger !== false) mount.append(open);
    mount.append(this.root);
    this.refresh();
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  get options(): ClientOptions {
    return { ...this.value };
  }

  setVisible(visible: boolean): void {
    // Re-read on every open: the Game Options panel persists to the same
    // blob, and a stale in-memory copy here would otherwise write its own
    // out-of-date values for fields it does not own back over Game Options'
    // latest choice the next time anything on this panel changes.
    if (visible) {
      this.value = loadClientOptions(this.storage);
      this.refresh();
    }
    this.root.hidden = !visible;
  }

  private bindRange(name: "music" | "sound", apply: (volume: number) => void): void {
    this.root.querySelector<HTMLInputElement>(`[data-option="${name}"]`)?.addEventListener("input", (event) => {
      const volume = Number((event.currentTarget as HTMLInputElement).value) / 100;
      this.value = name === "music"
        ? { ...this.value, musicVolume: volume }
        : { ...this.value, soundVolume: volume };
      apply(volume);
      this.refresh();
      this.persist();
    });
  }

  private refresh(): void {
    const music = this.root.querySelector<HTMLInputElement>('[data-option="music"]');
    const sound = this.root.querySelector<HTMLInputElement>('[data-option="sound"]');
    const graphics = this.root.querySelector<HTMLSelectElement>('[data-option="graphics"]');
    const camera = this.root.querySelector<HTMLSelectElement>('[data-option="camera"]');
    const track = this.root.querySelector<HTMLSelectElement>('[data-option="music-track"]');
    if (music !== null) music.value = String(Math.round(this.value.musicVolume * 100));
    if (sound !== null) sound.value = String(Math.round(this.value.soundVolume * 100));
    if (graphics !== null) graphics.value = this.value.graphicsQuality;
    if (camera !== null) camera.value = this.value.cameraDistance;
    if (track !== null) track.value = this.value.musicTrack;
    const musicOut = this.root.querySelector<HTMLOutputElement>('[data-output="music"]');
    const soundOut = this.root.querySelector<HTMLOutputElement>('[data-output="sound"]');
    if (musicOut !== null) musicOut.value = `${Math.round(this.value.musicVolume * 100)}%`;
    if (soundOut !== null) soundOut.value = `${Math.round(this.value.soundVolume * 100)}%`;
  }

  private persist(): void {
    const latest = loadClientOptions(this.storage);
    const merged: ClientOptions = {
      ...latest,
      musicVolume: this.value.musicVolume,
      musicTrack: this.value.musicTrack,
      soundVolume: this.value.soundVolume,
      graphicsQuality: this.value.graphicsQuality,
      cameraDistance: this.value.cameraDistance,
    };
    this.value = merged;
    saveClientOptions(merged, this.storage);
  }
}
